import { WALLET_ERROR_CODES, WalletError, type WalletErrorCode } from './errors';
import type { ProblemJson } from './types';
import type { Jwks } from './types';
import type {
  PasskeyRegisterStartRequest,
  PasskeyRegisterStartResponse,
  PasskeyRegisterFinishRequest,
  PasskeyRegisterFinishResponse,
  PasskeyLoginStartResponse,
  PasskeyLoginFinishRequest,
  PasskeyLoginFinishResponse,
} from './types';
import type { Application, ApplicationsFindRequest, ApplicationsSaveRequest } from './types';
import type { DelegationCreateRequest, DelegationCreateResponse } from './types';
import type { SessionResponse } from './types';
import { AccountsApi } from './accounts';

export interface ApiClientOptions {
  baseUrl: string;
  /** Provides the current JWT for JWT-authed endpoints. Called fresh per request. */
  getAuthToken?: () => string | null | Promise<string | null>;
  /** Override for testing or environments without global fetch. */
  fetch?: typeof fetch;
}

/** Extract WalletErrorCode from an RFC 7807 type URN. */
function codeFromUrn(urn: string | undefined): WalletErrorCode {
  if (!urn) return 'internal';
  const tail = urn.split('/').pop() || '';
  return (WALLET_ERROR_CODES as readonly string[]).includes(tail) ? (tail as WalletErrorCode) : 'internal';
}

/**
 * The ONLY runtime field check on this client, covering exactly TWO fields.
 *
 * `request()` hands back `data` through a bare `as T` — an unchecked cast is
 * the established pattern here, and a general per-field validator for every
 * response would be a large, duplicated second copy of the type layer. Two
 * fields earn the exception, for the same reason: neither has a safe default,
 * so an absent one is not "data we lost" but "a decision made wrongly".
 *
 * `isOrigin` decides whether a credential's PRF output IS the account root or
 * merely unwraps an envelope holding it, and an ABSENT `isOrigin` is falsy:
 * `@cef-ai/wallet-identity` takes the restrictive (non-origin) branch and
 * fails with `root-envelope-missing`, which reads to the user as "this passkey
 * cannot open your wallet".
 *
 * `accountId` is the account's identifier, and `accountFromSession`
 * (`@cef-ai/wallet-identity`) reads it unconditionally: an absent one
 * propagates `accountId: undefined` into `identity.account`, onto the
 * session-status wire, and into `EmbedWallet._accountId` — an identity object
 * that claims to name an account and does not.
 *
 * It must be a NON-EMPTY string, and that is not pedantry: absence and `""`
 * are two shapes of the SAME server-side failure, and only the wire tag
 * decides which shape reaches us. The server declares
 * `AccountID string` with a `json:"accountId,omitempty"` tag — under
 * `omitempty` an empty account id is simply left out, so a type check alone
 * happens to catch it. Drop `omitempty` (a plausible "fix" on the server side) and that same
 * empty id serialises as `"accountId": ""`, which satisfies
 * `typeof === 'string'` and every downstream `!== undefined`. A type-only
 * guard would therefore turn a loud failure into a silent one the day that tag
 * changes, leaving open the exact path this check exists to close. Rejecting
 * one shape and not the other is not half a guard; it is a guard with a date
 * on it.
 *
 * Against a real wire peer that dropped either field — an older server, a
 * field-stripping proxy — the downstream failure names the wrong component.
 * The safeguards are right; the attribution is wrong. So: check these two
 * fields, on the three endpoints that return a session, and name the RESPONSE
 * rather than the credential or the account. Nothing else.
 */
function assertSessionResponse<T>(data: T): T {
  const session = data as { isOrigin?: unknown; accountId?: unknown } | null | undefined;
  if (typeof session?.isOrigin !== 'boolean') {
    throw new WalletError(
      'session-response-malformed',
      'session response has no boolean `isOrigin`; the wallet API is missing the field or a proxy stripped it',
    );
  }
  if (typeof session.accountId !== 'string' || session.accountId === '') {
    throw new WalletError(
      'session-response-malformed',
      'session response has no non-empty string `accountId`; the wallet API is missing the field or a proxy stripped it',
    );
  }
  return data;
}

/**
 * Header the API requires on state-changing session endpoints as its CSRF
 * defence (wallet-api#14). The value is not a secret and is never checked —
 * only its presence matters, because presence alone is what forces the
 * preflight. Must stay in sync with the server's CORS `AllowHeaders`.
 */
export const CSRF_HEADER = 'X-CEF-Session';

export class ApiClient {
  private readonly fetchImpl: typeof fetch;
  readonly passkey: PasskeyApi;
  readonly applications: ApplicationsApi;
  readonly delegations: DelegationsApi;
  readonly jwks: JwksApi;
  readonly session: SessionApi;
  readonly accounts: AccountsApi;

  constructor(private readonly opts: ApiClientOptions) {
    this.fetchImpl = opts.fetch ?? globalThis.fetch.bind(globalThis);
    this.passkey = new PasskeyApi(this);
    this.applications = new ApplicationsApi(this);
    this.delegations = new DelegationsApi(this);
    this.jwks = new JwksApi(this);
    this.session = new SessionApi(this);
    this.accounts = new AccountsApi(this);
  }

  /**
   * Single HTTP method used by every endpoint namespace.
   * Owns: URL composition, header injection, request-ID, problem+json translation.
   */
  async request<T>(
    method: 'GET' | 'POST' | 'PATCH',
    path: string,
    init?: {
      body?: unknown;
      authRequired?: boolean;
      credentials?: RequestCredentials;
    },
  ): Promise<{ data: T; responseHeaders: Headers }> {
    const url = `${this.opts.baseUrl}${path}`;
    const requestId = crypto.randomUUID();
    const headers: Record<string, string> = {
      'X-Request-ID': requestId,
      // CSRF control for the cross-site deployment (wallet-api#14). With
      // SESSION_COOKIE_CROSS_SITE=true the refresh cookie is SameSite=None, so
      // the SameSite guard is gone and POST /v1/session/logout is a CORS
      // "simple request" any origin can forge with a form post — no preflight,
      // no readable response needed, because the damage (family revoked, user
      // logged out) is server-side. A non-safelisted header cannot be set
      // without a preflight, which the origin allowlist then refuses.
      //
      // Sent on EVERY request, not just /v1/session/*, on purpose: scoping it
      // by path in the client would duplicate the server's route list here,
      // and the day the two disagree the client omits the header and the
      // server answers 403. Unconditional cannot drift. It is also free —
      // X-Request-ID above is already non-safelisted, so these requests
      // already preflight.
      [CSRF_HEADER]: '1',
    };
    if (init?.body !== undefined) {
      headers['Content-Type'] = 'application/json';
    }
    if (init?.authRequired) {
      const token = await this.opts.getAuthToken?.();
      if (!token) {
        throw new WalletError('unauthorized', 'no auth token available', requestId);
      }
      headers['Authorization'] = `Bearer ${token}`;
    }

    let response: Response;
    try {
      response = await this.fetchImpl(url, {
        method,
        headers,
        body: init?.body !== undefined ? JSON.stringify(init.body) : undefined,
        credentials: init?.credentials,
      });
    } catch (cause) {
      throw new WalletError('internal', `network error: ${String(cause)}`, requestId);
    }

    if (!response.ok) {
      // Carried onto the WalletError so callers can tell a genuine absence
      // (404) from a service fault, which `code` alone cannot express for a
      // response with no problem+json body.
      const status = response.status;
      const contentType = response.headers.get('content-type') ?? '';
      if (contentType.includes('application/problem+json')) {
        const problem = (await response.json().catch(() => null)) as ProblemJson | null;
        throw new WalletError(codeFromUrn(problem?.type), problem?.detail, problem?.trace_id ?? requestId, status);
      }
      throw new WalletError('internal', `HTTP ${status}`, requestId, status);
    }

    const data = (await response.json().catch(() => ({}))) as T;
    return { data, responseHeaders: response.headers };
  }
}

// Namespaces are populated by later tasks. Stubs here keep the surface
// available to the constructor.

class PasskeyApi {
  constructor(protected readonly client: ApiClient) {}

  async registerStart(req: PasskeyRegisterStartRequest): Promise<PasskeyRegisterStartResponse> {
    const { data } = await this.client.request<PasskeyRegisterStartResponse>(
      'POST',
      '/v1/auth/passkey/register/start',
      { body: req },
    );
    return data;
  }

  async registerFinish(req: PasskeyRegisterFinishRequest): Promise<PasskeyRegisterFinishResponse> {
    const { data } = await this.client.request<PasskeyRegisterFinishResponse>(
      'POST',
      '/v1/auth/passkey/register/finish',
      // credentials:include so the browser stores the refresh-token Set-Cookie
      // from this cross-origin (wallet SPA → wallet-api) response. Without it,
      // the default same-origin mode discards the cookie and silent resume
      // never works. Mirrors session.refresh()/logout().
      { body: req, credentials: 'include' },
    );
    return assertSessionResponse(data);
  }

  async loginStart(): Promise<PasskeyLoginStartResponse> {
    const { data } = await this.client.request<PasskeyLoginStartResponse>('POST', '/v1/auth/passkey/login/start', {
      body: {},
    });
    return data;
  }

  async loginFinish(req: PasskeyLoginFinishRequest): Promise<PasskeyLoginFinishResponse> {
    const { data } = await this.client.request<PasskeyLoginFinishResponse>('POST', '/v1/auth/passkey/login/finish', {
      body: req,
      // credentials:include so the browser stores the refresh-token Set-Cookie
      // from this cross-origin (wallet SPA → wallet-api) response. Without it,
      // the default same-origin mode discards the cookie and silent resume
      // never works. Mirrors session.refresh()/logout().
      credentials: 'include',
    });
    return assertSessionResponse(data);
  }
}

class ApplicationsApi {
  constructor(protected readonly client: ApiClient) {}

  async find(req: ApplicationsFindRequest): Promise<Application[]> {
    const { data } = await this.client.request<Application[]>('POST', '/v1/applications/find', {
      body: req,
      authRequired: true,
    });
    return data;
  }

  async save(req: ApplicationsSaveRequest): Promise<Application> {
    const { data } = await this.client.request<Application>('POST', '/v1/applications', {
      body: req,
      authRequired: true,
    });
    return data;
  }
}

class DelegationsApi {
  constructor(protected readonly client: ApiClient) {}

  async create(req: DelegationCreateRequest): Promise<DelegationCreateResponse> {
    const { data } = await this.client.request<DelegationCreateResponse>('POST', '/v1/delegations', {
      body: req,
      authRequired: true,
    });
    return data;
  }
}

const DEFAULT_JWKS_TTL_SECONDS = 300;

function parseMaxAge(cacheControl: string | null): number {
  if (!cacheControl) return DEFAULT_JWKS_TTL_SECONDS;
  const match = cacheControl.match(/max-age=(\d+)/i);
  return match ? Number(match[1]) : DEFAULT_JWKS_TTL_SECONDS;
}

class JwksApi {
  private cache: { value: Jwks; expiresAtMs: number } | null = null;

  constructor(protected readonly client: ApiClient) {}

  async fetch(): Promise<Jwks> {
    const now = Date.now();
    if (this.cache && now < this.cache.expiresAtMs) {
      return this.cache.value;
    }

    const { data, responseHeaders } = await this.client.request<Jwks>('GET', '/v1/.well-known/jwks.json');

    const ttlSeconds = parseMaxAge(responseHeaders.get('Cache-Control'));
    this.cache = { value: data, expiresAtMs: now + ttlSeconds * 1000 };
    return data;
  }

  clear(): void {
    this.cache = null;
  }
}

/**
 * Pause between the two `/v1/session/refresh` attempts. Short on purpose: the
 * caller is either an idle tab about to prompt for a passkey or a booting
 * iframe deciding whether it has a session, and both are waiting on it.
 */
const SESSION_REFRESH_RETRY_DELAY_MS = 250;

/**
 * Is this refresh failure worth one more attempt?
 *
 * ONLY a 5xx. Verified against wallet-api rather than assumed, because the
 * decline below rests entirely on the asymmetry: a 5xx leaves the token family
 * in a state where retrying can only help, and silence does not.
 *
 * `SessionHandler.Refresh` answers 5xx from three places, and none of them
 * leaves a live successor the browser has not got:
 *
 *  - before rotation, when a lookup or read fails — nothing was consumed;
 *  - `MarkRotated` succeeded and the successor `Insert` failed. Those are two
 *    statements, deliberately not one transaction, so the family is left with
 *    NO live token. A retry inside the grace window mints the successor that
 *    never existed — it RECOVERS a session that would otherwise be found dead
 *    minutes later, outside the window, and burned;
 *  - after rotation, when the credential lookup fails. `SetRefreshCookie` has
 *    already run and a browser stores `Set-Cookie` on a 500 like any other
 *    response, so the retry presents the NEW token and simply succeeds.
 *
 * wallet-api is built expecting this. Its 500 path deliberately does not clear
 * the cookie — "otherwise a one-off DB blip would force the user back through
 * WebAuthn" (`internal/api/handlers/session.go`) — so the retry is the client
 * half of a decision the server already made.
 *
 * A NETWORK error is the opposite case, and CEF-AI/wallet#60's own suggestion
 * to include it is declined. When no response arrives the request may have
 * fully SUCCEEDED — rotated, successor inserted, `Set-Cookie` written — and
 * only the reply was lost, leaving a live successor in the family and the OLD
 * cookie in the browser. Re-sending it replays a rotated token against a family
 * that already holds a live one: grace would mint a SECOND, which is precisely
 * the two-live-tokens state the rotation design avoids by ordering its writes
 * the way it does, and once the 2-use budget is spent (or the window disabled)
 * reuse detection burns the family.
 *
 * That budget is shared with the legitimate concurrent refreshes it exists for
 * — two tabs; an iframe and a ceremony popup — and the outcomes are not
 * comparable: not retrying costs one passkey prompt, retrying wrongly logs the
 * user out of everything. So we take the prompt. (Devnet sets
 * `SESSION_REFRESH_GRACE=10s` / `SESSION_REFRESH_GRACE_MAX=2` explicitly;
 * testnet takes the same collection defaults.)
 *
 * `status` is `undefined` for both a network error and every client-side
 * `WalletError` (a malformed-body rejection from `assertSessionResponse`, say),
 * which is why this keys on the number rather than on the error code.
 */
function isRetryableRefreshFault(e: unknown): boolean {
  return e instanceof WalletError && typeof e.status === 'number' && e.status >= 500;
}

class SessionApi {
  constructor(protected readonly client: ApiClient) {}

  /**
   * Silently resumes a session using the wallet-origin refresh cookie. No
   * Authorization header.
   *
   * **Retried once, on a 5xx only** (CEF-AI/wallet#60). Every caller treats a
   * rejected refresh as "no session": `IdentityImpl.getJwt()` falls through to
   * a WebAuthn ceremony, and the embedded wallet's boot resume gives up and
   * renders signed-out. A transient fault is indistinguishable from a dead
   * cookie at those call sites, so a single 500 turns a perfectly live session
   * into a passkey prompt.
   *
   * The prompt appears only when the fault is brief enough for the NEXT call to
   * succeed — `login()` starts with `passkey/login/start` against the same API,
   * so during a real outage the ceremony dies before any prompt is shown. That
   * narrow window is exactly what one retry closes.
   *
   * See `isRetryableRefreshFault` for why a network error is not retried.
   */
  async refresh(): Promise<SessionResponse> {
    try {
      return await this.refreshOnce();
    } catch (e) {
      if (!isRetryableRefreshFault(e)) throw e;
      // Logged EVERY time, not once per process: #60 asked for evidence of how
      // often this happens before deciding the fault was worth handling, and a
      // deduplicated line answers "does it ever" while hiding "how often".
      // Says nothing about what the caller does next: from `getJwt()` the
      // fallback is a WebAuthn ceremony, from the embedded wallet's boot resume
      // it is rendering signed-out. Naming one would misdirect anyone debugging
      // the other.
      console.warn(`[wallet] /v1/session/refresh answered ${(e as WalletError).status}; retrying once`);
      await new Promise((resolve) => setTimeout(resolve, SESSION_REFRESH_RETRY_DELAY_MS));
      return this.refreshOnce();
    }
  }

  private async refreshOnce(): Promise<SessionResponse> {
    const { data } = await this.client.request<SessionResponse>('POST', '/v1/session/refresh', {
      credentials: 'include',
    });
    return assertSessionResponse(data);
  }

  /** Clears the refresh cookie server-side. No Authorization header. */
  async logout(): Promise<void> {
    await this.client.request<void>('POST', '/v1/session/logout', {
      credentials: 'include',
    });
  }
}
